import {inject} from "@angular/core";
import {ActivatedRouteSnapshot, RouterStateSnapshot, Routes} from "@angular/router";
import {AuthGuard} from "./auth/auth.guard";
import {appLandingRedirect} from "./auth/landing-redirect";
import {PermissionGuard} from "./auth/permission.guard";
import {SystemPermissions} from "./auth/system-permissions";

export const routes: Routes = [
    {
        path: 'login',
        loadComponent: () => import('./login/login.component').then(m => m.LoginComponent)
    },
    {
        path: 'app',
        loadComponent: () => import('./layout/app.layout.component').then(m => m.AppLayoutComponent),
        canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)],
        canActivateChild: [
            (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivateChild(route, state),
            (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(PermissionGuard).canActivateChild(route, state)
        ],
        children: [
            {
                path: 'oms',
                loadChildren: () => import('./oms/oms.routes').then(m => m.omsRoutes),
                data: {breadcrumb: "OMS"}
            },
            {
                path: 'pms',
                loadChildren: () => import('./pms/pms.routes').then(m => m.pmsRoutes),
                data: {breadcrumb: "PMS", requiredPermissions: [SystemPermissions.CanVisitPms]}
            },
            {
                path: 'rms',
                loadChildren: () => import('./rms/rms.routes').then(m => m.rmsRoutes),
                data: {breadcrumb: "RMS", requiredPermissions: [SystemPermissions.CanVisitRms]}
            },
            {
                path: 'fa',
                loadChildren: () => import('./fa/fa.routes').then(m => m.faRoutes),
                data: {breadcrumb: "FA", requiredPermissions: [SystemPermissions.CanVisitFa]}
            },
            {
                path: 'crm',
                loadChildren: () => import('./crm/crm.routes').then(m => m.crmRoutes),
                data: {breadcrumb: "CRM", requiredPermissions: [SystemPermissions.CanVisitCrm]}
            },
            {
                path: 'tms',
                loadChildren: () => import('./tms/tms.routes').then(m => m.tmsRoutes),
                data: {breadcrumb: "TMS", requiredPermissions: [SystemPermissions.CanVisitTms]}
            },
            {
                path: 'hr',
                loadChildren: () => import('./hr/hr.routes').then(m => m.hrRoutes),
                data: {breadcrumb: "HR", requiredPermissions: [SystemPermissions.CanVisitHr]}
            },
            {
                // Alarms is a PMS feature but is intentionally routed standalone (top-level `alarms`,
                // not nested under the PMS shell) — it has its own breadcrumb/permission and no shared
                // PMS layout. Data wiring uses PmsService (see services/backend/pms.service.ts).
                path: 'alarms',
                data: {breadcrumb: "Feed alarms", requiredPermissions: [SystemPermissions.PmsAlarmsRead]},
                children: [
                    {
                        path: '',
                        loadComponent: () => import('./alarms/alarms.component').then(c => c.AlarmsComponent),
                    },
                    {
                        // Like risk-rules, this must precede `:id` so it is not captured as an alarm id.
                        path: 'deleted',
                        loadComponent: () => import('./alarms/deleted-alarms/deleted-alarms.component')
                            .then(c => c.DeletedAlarmsComponent),
                        data: {breadcrumb: "Deleted alarms"},
                    },
                    {
                        // Risk rules must precede the `:id` param route so it is not captured as an alarm id.
                        path: 'risk-rules',
                        data: {breadcrumb: "Risk Rules", requiredPermissions: [SystemPermissions.PmsRiskRulesRead]},
                        children: [
                            {
                                path: '',
                                loadComponent: () => import('./alarms/risk-rules/risk-rules.component').then(c => c.RiskRulesComponent),
                                data: {root: true}, // top-level list — no back arrow in the content header
                            },
                            {
                                path: ':id',
                                loadComponent: () => import('./alarms/risk-rules/display-risk-rule/display-risk-rule.component').then(m => m.DisplayRiskRuleComponent),
                                data: {breadcrumb: "View Risk Rule"},
                            },
                        ],
                    },
                    {
                        path: ':id',
                        loadComponent: () => import('./alarms/display-alarm/display-alarm.component').then(m => m.DisplayAlarmComponent),
                        data: {breadcrumb: "View Alarm"},
                    },
                ]
            },
            {
                path: 'admin',
                loadChildren: () => import('./admin/admin.routes').then(m => m.adminRoutes),
                data: {breadcrumb: "Admin", requiredPermissions: [SystemPermissions.CanVisitAdmin]}
            },
            {
                path: 'wof',
                loadChildren: () => import('./wof/wof.routes').then(m => m.wofRoutes),
                data: {breadcrumb: 'WOF', requiredPermissions: [SystemPermissions.WofRead]}
            },
            {
                path: 'hft',
                loadChildren: () => import('./hft/hft.routes').then(m => m.hftRoutes),
                data: {breadcrumb: 'HFT', requiredPermissions: [SystemPermissions.HftRead]}
            },
            {
                path: 'docs',
                loadChildren: () => import('./docs/docs.routes').then(m => m.docsRoutes),
                data: {breadcrumb: 'Docs'}
            },
            {
                path: 'theme',
                loadComponent: () => import('./theme-settings/theme-settings.component').then(m => m.ThemeSettingsComponent),
                data: {breadcrumb: 'Theme settings'}
            },
            {path: '', pathMatch: 'full', redirectTo: appLandingRedirect},
        ]
    },
    {path: 'notfound', loadComponent: () => import('./notfound/notfound.component').then(m => m.NotfoundComponent)},
    {path: 'noperm', loadComponent: () => import('./noperm/noperm.component').then(m => m.NopermComponent)},
    {path: '', redirectTo: '/app', pathMatch: 'full'},
    {path: '**', redirectTo: '/notfound'},
];
